import { Box, Card, CardContent, Chip, Grid, Pagination, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ColorService from "../../../services/ColorService";
import UtilService from "../../../services/UtilService";

export default function TopicCard({ topic }) {
    const navigate = useNavigate();
    const [templatePage, setTemplatePage] = useState(1);
    const templatesPerPage = 6; // Show 6 templates per page (2 rows of 3)

    // Reset template page when topic changes
    useEffect(() => {
        setTemplatePage(1);
    }, [topic.id]);

    const handleClick = () => {
        navigate(`/admin/topic/detail/${topic.id}`);
    };

    const getGenreDisplay = () => {
        if (!topic.genre) return "";
        if (topic.genre.parentGenre) {
            return `${topic.genre.parentGenre.name} → ${topic.genre.name}`;
        }
        return topic.genre.name;
    };

    const getRelatedWordsCount = () => {
        if (!topic.relatedWords) return 0;
        return topic.relatedWords.split("\n").length;
    };

    const getDifficultyColor = () => {
        return ColorService.getDifficultyColor(topic.difficultyLevel);
    };

    const getStateColor = () => {
        // Check if state is directly on topic, or in hiddenTpl array
        const state = topic.state || (topic.hiddenTpl && topic.hiddenTpl.length > 0 ? topic.hiddenTpl[0].state : null);
        return ColorService.getStateColor(state);
    };

    const getStateLabel = () => {
        const state = topic.state || (topic.hiddenTpl && topic.hiddenTpl.length > 0 ? topic.hiddenTpl[0].state : null);
        if (!state) return null;
        // Format state: DRAFT -> Draft, READY_TO_REVIEW -> Ready To Review, etc.
        return state.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ');
    };

    const getTemplates = () => {
        if (!topic.hiddenTpl || topic.hiddenTpl.length === 0) return [];
        return topic.hiddenTpl;
    };

    const handleTemplatePageChange = (event, value) => {
        setTemplatePage(value);
        event.stopPropagation(); // Prevent card click
    };

    const templates = getTemplates();
    const totalTemplatePages = Math.ceil(templates.length / templatesPerPage);
    const startIndex = (templatePage - 1) * templatesPerPage;
    const endIndex = startIndex + templatesPerPage;
    const displayedTemplates = templates.slice(startIndex, endIndex);

    const getTemplateStateColor = (template) => {
        return ColorService.getStateColor(template.state);
    };

    const getTemplateStateLabel = (template) => {
        if (!template.state) return null;
        return template.state.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ');
    };

    return (
        <Card
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                    boxShadow: 6,
                    transform: "translateY(-2px)",
                },
            }}
            onClick={handleClick}
        >
            <CardContent sx={{ flexGrow: 1 }}>
                <Typography 
                    variant="h6" 
                    component="h3" 
                    sx={{ 
                        mb: 1.5, 
                        fontWeight: "medium",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minHeight: "3.5em",
                        lineHeight: "1.75em"
                    }}
                >
                    {topic.title}
                </Typography>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    {topic.genre && (
                        <Chip
                            label={getGenreDisplay()}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    )}
                    <Chip
                        label={`${getRelatedWordsCount()} words`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                    />
                    {topic.difficultyLevel && (
                        <Chip
                            label={topic.difficultyLevel}
                            size="small"
                            sx={{
                                backgroundColor: getDifficultyColor(),
                                color: "white",
                                fontWeight: "medium"
                            }}
                        />
                    )}
                    {getStateLabel() && (
                        <Chip
                            label={getStateLabel()}
                            size="small"
                            sx={{
                                backgroundColor: getStateColor(),
                                color: "white",
                                fontWeight: "medium"
                            }}
                        />
                    )}
                </Box>

                {topic.tags && Array.isArray(topic.tags) && topic.tags.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
                        {topic.tags.map((tag, index) => (
                            <Chip
                                key={index}
                                label={typeof tag === "string" ? tag : tag?.tag ?? String(tag)}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: "0.7rem", height: 22 }}
                            />
                        ))}
                    </Box>
                )}

                {templates.length > 0 && (
                    <Box sx={{ mt: 2, mb: 1 }} onClick={(e) => e.stopPropagation()}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                            Hidden Templates ({templates.length}):
                        </Typography>
                        <Grid container spacing={1}>
                            {displayedTemplates.map((template, index) => (
                                <Grid key={template.id || index} size={{ xs: 4 }}>
                                    <Box
                                        sx={{
                                            p: 0.5,
                                            borderRadius: 1,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            backgroundColor: "background.paper",
                                            textAlign: "center"
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: "block",
                                                fontSize: "0.7rem",
                                                fontWeight: "medium",
                                                mb: 0.5,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap"
                                            }}
                                            title={template.name || "Template"}
                                        >
                                            {template.name || "Template"}
                                        </Typography>
                                        {getTemplateStateLabel(template) && (
                                            <Chip
                                                label={getTemplateStateLabel(template)}
                                                size="small"
                                                sx={{
                                                    backgroundColor: getTemplateStateColor(template),
                                                    color: "white",
                                                    fontSize: "0.65rem",
                                                    height: "18px",
                                                    "& .MuiChip-label": {
                                                        px: 0.5
                                                    }
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                        {totalTemplatePages > 1 && (
                            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                                <Pagination
                                    count={totalTemplatePages}
                                    page={templatePage}
                                    onChange={handleTemplatePageChange}
                                    size="small"
                                    color="primary"
                                    sx={{
                                        "& .MuiPaginationItem-root": {
                                            minWidth: "28px",
                                            height: "28px",
                                            fontSize: "0.75rem"
                                        }
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    Created: {UtilService.formatDate(topic.createdAt)}
                </Typography>
            </CardContent>
        </Card>
    );
}

